import { Injectable, inject } from '@angular/core';

import { BookingSdk } from '@libs/graphql/sdk/booking/booking.sdk';
import { BookingPackageSdk } from '@libs/graphql/sdk/booking-package/booking-package.sdk';
import { CustomerSdk } from '@libs/graphql/sdk/customer/customer.sdk';
import { ReviewSdk } from '@libs/graphql/sdk/review/review.sdk';
import { VendorSdk } from '@libs/graphql/sdk/vendor/vendor.sdk';
import type { BookingPoco, BookingPackagePoco, ReviewPoco, VendorPoco } from '@libs/graphql/models';

import { buildAuditFields, newEntityGuid } from '@shared/utils/audit-fields';
import { localNowForDb } from '@shared/utils/local-time';
import {
  buildReviewFormModel, emptyReviewFormModel, type ReviewFormModel,
} from '@shared/utils/review-form.util';

/** What the customer typed into the form: the overall line plus a per-offering entry. */
export interface ReviewDraft {
  overall: { stars: number; text: string };
  services: Record<number, { stars: number; note: string }>;
}

/**
 * Loads and writes the customer's review for one booking.
 *
 * The form is reachable from two places — the booking detail's "How was your event?" card and the
 * "Leave a review" chip on the bookings LIST, which opens it as a popup without leaving the list —
 * and the write is subtle enough (the rating check constraint, preserving the vendor's public reply
 * through an edit) that both must go through one implementation.
 */
@Injectable({ providedIn: 'root' })
export class ReviewFormService {
  private readonly bookingSdk = inject(BookingSdk);
  private readonly packageSdk = inject(BookingPackageSdk);
  private readonly customerSdk = inject(CustomerSdk);
  private readonly reviewSdk = inject(ReviewSdk);
  private readonly vendorSdk = inject(VendorSdk);

  /**
   * Build the form for one booking, straight from the database. Used by the bookings LIST, whose
   * own load deliberately carries none of this — a popup opened on demand fetches what it needs on
   * demand, rather than making every list load pay for a form most visits never open. The booking
   * detail already holds all of it and builds the same model through `buildReviewFormModel`.
   */
  async load(bookingId: number, customerId: number): Promise<ReviewFormModel> {
    if (!bookingId) return emptyReviewFormModel();
    const booking = await this.loadBooking(bookingId, customerId);
    if (!booking) return emptyReviewFormModel();
    const [packages, reviews, customerName] = await Promise.all([
      this.loadPackages(bookingId),
      this.loadReviews(bookingId),
      this.loadCustomerName(booking.customerId),
    ]);
    // Every vendor earning on the booking, not just the reseller: a partner-owned service is rated
    // against the partner, and the form names them under the service.
    const vendorIds = Array.from(new Set(
      [booking.resellerVendorId, ...packages.map(p => p.vendorId)].filter((v): v is number => !!v),
    ));
    const vendors = await this.loadVendors(vendorIds);
    return buildReviewFormModel({
      booking,
      packages: packages.filter(p => p.status !== 'removed' && p.status !== 'cancelled'),
      reviews,
      vendorName: id => vendors.find(v => v.id === id)?.name ?? '',
      customerName,
    });
  }

  /**
   * Write the review: one Review row per rated service (attributed to the vendor who delivers it)
   * plus the overall row for the reseller — the same shape the vendor pages read back.
   *
   * A service left at 0 stars is skipped, not written as a zero: CK_Review_Rating only allows 1..5,
   * so "didn't rate this one" is the absence of a row. Existing rows are re-saved in full (the
   * upsert nulls any column it isn't given), which keeps the vendor's public reply and the original
   * createdDate intact through an edit.
   */
  async save(args: {
    model: ReviewFormModel;
    draft: ReviewDraft;
    bookingId: number;
    customerId: number;
    userId: number;
  }): Promise<void> {
    const { model, draft, bookingId, customerId, userId } = args;
    const existing = await this.existingReviews(bookingId);
    const now = localNowForDb();
    const upsert = async (vendorId: number, offeringId: number | null, rating: number, text: string) => {
      const prev = existing.find(x => ((x['offeringId'] as number | null) ?? null) === offeringId);
      const res = await this.reviewSdk.save({
        ...(prev ?? {}),
        id: prev ? prev['id'] : 0,
        guid: prev ? prev['guid'] : newEntityGuid(),
        vendorId,
        offeringId,
        customerId,
        bookingId,
        author: model.authorName,
        rating,
        reviewText: text.trim(),
        isDeleted: false,
        ...(prev ? {} : buildAuditFields(userId)),
        updatedBy: userId,
        updatedDate: now,
      } as never);
      if (!res.success) throw new Error(res.error || res.message || 'Could not save your review');
    };

    for (const s of model.services) {
      const d = draft.services[s.offeringId ?? 0];
      if (!d?.stars) continue;
      await upsert(s.ownerVendorId, s.offeringId, d.stars, d.note);
    }
    await upsert(model.overall.ownerVendorId, null, draft.overall.stars, draft.overall.text);
  }

  /** This booking's review rows as FULL records, ready to spread into an update. */
  private async existingReviews(bookingId: number): Promise<Array<Record<string, unknown>>> {
    const res = await this.reviewSdk.list(
      { bookingId: { eq: bookingId } }, [{ column: 'id', desc: false }],
      ['id', 'guid', 'vendorId', 'offeringId', 'customerId', 'bookingId', 'author', 'rating',
       'reviewText', 'replyText', 'replyDate', 'isDeleted', 'createdBy', 'createdDate'],
      1, 50,
    );
    return ((res.data ?? []) as unknown as Array<Record<string, unknown>>);
  }

  /** Scoped to the signed-in customer — a booking id must not open somebody else's form. */
  private async loadBooking(bookingId: number, customerId: number): Promise<BookingPoco | null> {
    const res = await this.bookingSdk.list(
      { id: { eq: bookingId }, customerId: { eq: customerId } }, [{ column: 'id', desc: false }],
      ['id', 'customerId', 'resellerVendorId', 'eventDate', 'contactName', 'status'], 1, 1,
    );
    return ((res.data as BookingPoco[]) ?? [])[0] ?? null;
  }

  private async loadPackages(bookingId: number): Promise<BookingPackagePoco[]> {
    const res = await this.packageSdk.list(
      { bookingId: { eq: bookingId } }, [{ column: 'id', desc: false }],
      ['id', 'bookingId', 'vendorId', 'offeringId', 'name', 'status'], 1, 200,
    );
    return (res.data as BookingPackagePoco[]) ?? [];
  }

  private async loadReviews(bookingId: number): Promise<ReviewPoco[]> {
    const res = await this.reviewSdk.list(
      { bookingId: { eq: bookingId } }, [{ column: 'id', desc: false }],
      ['id', 'bookingId', 'offeringId', 'rating', 'reviewText', 'replyText', 'isDeleted'], 1, 50,
    );
    return (res.data as ReviewPoco[]) ?? [];
  }

  private async loadVendors(vendorIds: number[]): Promise<VendorPoco[]> {
    if (!vendorIds.length) return [];
    const res = await this.vendorSdk.list(
      { id: { in: vendorIds } }, [{ column: 'id', desc: false }], ['id', 'name'], 1, 200,
    );
    return (res.data as VendorPoco[]) ?? [];
  }

  private async loadCustomerName(customerId?: number | null): Promise<string> {
    if (!customerId) return '';
    const res = await this.customerSdk.list(
      { id: { eq: customerId } }, [{ column: 'id', desc: false }], ['id', 'name'], 1, 1,
    );
    return (((res.data ?? []) as unknown as Array<Record<string, unknown>>)[0]?.['name'] as string) ?? '';
  }
}
